import React, { useState, useCallback } from 'react';
import { useRefresh, useNotify } from 'react-admin';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';

import { uploadMarkdowns } from './uploadMarkdowns';
import { requirePartner } from '@src/providers/dataProvider/shared';

interface UploadMarkdownsDialogProps {
  open: boolean;
  onClose: () => void;
}

const REQUIRED_COLUMNS = [
  'parent_sku',
  'sku_subcode',
  'color',
  'size_code',
  'yadjusted_code',
  'yadjusted_price',
] as const;

const UploadMarkdownsDialog = ({ open, onClose }: UploadMarkdownsDialogProps) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleClose = useCallback(() => {
    setFile(null);
    setIsUploading(false);
    onClose();
  }, [onClose]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop()?.toLowerCase();
    if (ext !== 'csv' && ext !== 'txt') {
      notify('Only .csv and .txt files are accepted', { type: 'error' });
      e.target.value = '';
      return;
    }

    setFile(selectedFile);
  }, [notify]);

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setIsUploading(true);
    try {
      const partner = requirePartner();
      await uploadMarkdowns({ partnerId: partner.id, file });
      notify('Upload successful', { type: 'success' });
      refresh();
      handleClose();
    } catch (error) {
      console.error('UploadMarkdownsDialog: upload failed', {
        error: error instanceof Error ? error.message : error,
      });
      const message = error instanceof Error ? error.message : 'Upload failed';
      notify(`Error: ${message}`, { type: 'error' });
      setIsUploading(false);
    }
  }, [file, handleClose, notify, refresh]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Markdowns CSV</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Required Columns</AlertTitle>
          <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
            {REQUIRED_COLUMNS.join(', ')}
          </Typography>
        </Alert>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Express yadjusted_price in cents (e.g. $40.00 as 4000).
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            yadjusted_code is required for non-zero prices. If sku_subcode, color,
            or size_code is null, they are not applied.
          </Typography>
        </Box>
        <Button variant="outlined" component="label" disabled={isUploading}>
          {file ? file.name : 'Choose File'}
          <input type="file" hidden accept=".csv,.txt" onChange={handleFileChange} />
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || isUploading}
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadMarkdownsDialog;
